import { inject, Injectable } from '@angular/core';
import { ECapabilities, EInvoiceType, IInvoice } from '@okcargo-apps/shared-domain-ionic';
import { CompanyQueryService, DateService } from '@okcargo-apps/shared-infrastructure-ionic';
import { FilterPayload, InvoiceMetadataFieldNames, OrderType, SearchInvoicesQuery } from '@okcargo/query-processor';
import { BehaviorSubject, from, map, Observable } from 'rxjs';
import { AccountService } from '../account/account.service';
import { DownloadDocumentService } from '../download-document/download-document.service';

@Injectable({
  providedIn: 'root',
})
export class InvoiceService {
  private readonly companyQueryService = inject(CompanyQueryService);
  private readonly downloadDocumentService = inject(DownloadDocumentService);
  private readonly accountService = inject(AccountService);
  private readonly dateService = inject(DateService);

  public invoicesPurchaseLoaded = false;
  private invoicesPurchasePageIndex = 0;
  private readonly invoicesPurchasePageSize = 10;
  private readonly _invoicesPurchase$ = new BehaviorSubject<IInvoice[]>([]);

  public invoicesSaleLoaded = false;
  private readonly invoicesSalePageIndex = 0;
  private readonly invoicesSalePageSize = 10;
  private readonly _invoicesSale$ = new BehaviorSubject<IInvoice[]>([]);

  private readonly account = this.accountService.account;

  // constructor() {}

  get invoicesPurchase$(): Observable<IInvoice[]> {
    if (!this.invoicesPurchaseLoaded) {
      this.getInvoices(EInvoiceType.PURCHASE, this.invoicesPurchasePageIndex, this.invoicesPurchasePageSize);
    }
    return this._invoicesPurchase$.asObservable();
  }

  get invoicesSale$(): Observable<IInvoice[]> {
    if (!this.invoicesSaleLoaded) {
      this.getInvoices(EInvoiceType.SALE, this.invoicesSalePageIndex, this.invoicesSalePageSize);
    }
    return this._invoicesSale$.asObservable();
  }

  public refreshInvoices(): void {
    this.getInvoices(EInvoiceType.PURCHASE, this.invoicesPurchasePageIndex, this.invoicesPurchasePageSize);
  }

  // private getInvoices(type: EInvoiceType, pageIndex: number, pageSize: number): void {
  //   if (this.account.capabilities.includes(ECapabilities.ADMINISTRATIVE_DOCUMENTS)) {
  //     const filters = [
  //       {
  //         field: 'type',
  //         comparator: FilterComparator.EQ,
  //         value: type,
  //       },
  //     ];

  //     const query = {
  //       criteriaPayload: {
  //         pageIndex: pageIndex,
  //         pageSize: pageSize,
  //         order: [
  //           {
  //             field: InvoiceMetadataFieldNames.DATE,
  //             type: OrderType.DESC,
  //           },
  //         ],
  //         filters: filters as unknown as FilterPayload,
  //       },
  //     } as unknown as SearchInvoicesQuery;

  //     // console.log('type', type);
  //     // console.log('query', query);

  //     from(this.companyQueryService.searchInvoices(query))
  //       .pipe(
  //         map((invoices: any[]) =>
  //           invoices.map((invoice: any) => ({
  //             id: invoice.id,
  //             name: invoice.name,
  //             // TODO dates should come in ISO 8601 format YYYY-MM-DDTHH:mm:ss.sssZ
  //             date: this.dateService.parseDateIfNeeded(invoice.date), // comes in format dd/MM/yyyy
  //             dateDue: this.dateService.parseDateIfNeeded(invoice.dateDue), // comes in format dd/MM/yyyy
  //             amountTotal: invoice.totalPrice,
  //             amountTax: invoice.tax,
  //             amountUntaxed: invoice.basePrice,
  //             state: invoice.status,
  //             downloadUrl: this.downloadDocumentService.getDownloadDocumentURL(invoice.documentId),
  //           })),
  //         ),
  //       )
  //       .subscribe({
  //         next: (invoices) => {
  //           let currentInvoices: any[] = [];

  //           switch (type) {
  //             case EInvoiceType.PURCHASE:
  //               currentInvoices = this._invoicesPurchase$.getValue();
  //               this._invoicesPurchase$.next([...currentInvoices, ...invoices]);
  //               this.invoicesPurchaseLoaded = true;
  //               break;

  //             case EInvoiceType.SALE:
  //               currentInvoices = this._invoicesSale$.getValue();
  //               this._invoicesSale$.next([...currentInvoices, ...invoices]);
  //               this.invoicesSaleLoaded = true;
  //               break;

  //             default:
  //               console.error('Unknown invoice type');
  //           }
  //         },
  //         error: (error) => console.error('Error loading invoices', error),
  //       });
  //   }
  // }

  private getInvoices(type: EInvoiceType, pageIndex: number, pageSize: number): void {
    if (this.accountService.hasCapability(ECapabilities.ADMINISTRATIVE_DOCUMENTS)) {
      const query = {
        criteriaPayload: {
          pageIndex: pageIndex,
          pageSize: pageSize,
          order: [
            {
              field: InvoiceMetadataFieldNames.DATE,
              type: OrderType.DESC,
            },
          ],
          filters: [] as unknown as FilterPayload,
        },
      } as unknown as SearchInvoicesQuery;

      const request$ =
        type === EInvoiceType.PURCHASE
          ? from(this.companyQueryService.searchInvoicesForCarrier(query))
          : from(this.companyQueryService.searchInvoicesForShipper(query));

      request$
        .pipe(
          map((invoices: any[]) =>
            invoices.map((invoice: any) => ({
              id: invoice.id,
              name: invoice.number,
              // TODO dates should come in ISO 8601 format YYYY-MM-DDTHH:mm:ss.sssZ
              date: this.dateService.parseDateIfNeeded(invoice.date),
              dateDue: this.dateService.parseDateIfNeeded(invoice.dateDue),
              amountTotal: invoice.totalPrice,
              amountTax: invoice.tax,
              amountUntaxed: invoice.basePrice,
              state: invoice.status,
              documentId: invoice.documentId,
            })),
          ),
        )
        .subscribe({
          next: (invoices) => {
            let currentInvoices: any[] = [];

            switch (type) {
              case EInvoiceType.PURCHASE:
                currentInvoices = this._invoicesPurchase$.getValue();
                this._invoicesPurchase$.next([...currentInvoices, ...invoices]);
                this.invoicesPurchaseLoaded = true;
                break;

              case EInvoiceType.SALE:
                currentInvoices = this._invoicesSale$.getValue();
                this._invoicesSale$.next([...currentInvoices, ...invoices]);
                this.invoicesSaleLoaded = true;
                break;

              default:
                console.error('Unknown invoice type');
            }
          },
          error: (error) => console.error(`Error loading ${type} invoices:`, error),
        });
    }
  }

  public loadMoreInvoices(): void {
    this.invoicesPurchasePageIndex++;
    this.getInvoices(EInvoiceType.PURCHASE, this.invoicesPurchasePageIndex, this.invoicesPurchasePageSize);
  }
}
